import React, { useEffect, useMemo, useCallback, useState, useContext } from 'react';
import Loader from '../../../general/Loader';
import AvailabilityIcon from '../../../general/AvailabilityIcon';
import moment from 'moment';
import { replaceContent } from '../../../../utils/dom';
import Settings from './Settings';
import { isMobileOnly } from 'react-device-detect';
import {
  defaultInterval,
  computeRelevantIntervals,
  computeFullCalendarEvents,
  viewTypeInitialRange,
  renderEventContent
} from './utils';
import { isFullDayInterval } from '../../../../utils/time';
import { debounce, isEmpty } from 'lodash';

// FullCalendar
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import nlLocale from '@fullcalendar/core/locales/nl';
import daLocale from '@fullcalendar/core/locales/da';
import enLocale from '@fullcalendar/core/locales/en-gb';
import interactionPlugin from '@fullcalendar/interaction';

const plugins = [dayGridPlugin, listPlugin, timeGridPlugin, interactionPlugin];

// Redux related
import { connect } from 'react-redux';
import { selectors as availabilityCodeSelectors } from '../../../../state/modules/availability-codes';
import { selectors as functionSelectors } from '../../../../state/modules/standby-duty-functions';
import { selectors as combinedScheduleSelectors } from '../../../../state/modules/combined-schedules';
import { selectors as membershipSelectors } from '../../../../state/modules/memberships';
import { operations as combinedScheduleOperations } from '../../../../state/modules/combined-schedules';
import { selectors as groupSelectors } from '../../../../state/modules/groups';
import { selectors as userSelectors } from '../../../../state/modules/users';
import { operations as userOperations } from '../../../../state/modules/users';
import { getCurrentUser } from '../../../../state/modules/current-user/operations';
import { selectCurrentUserId } from '../../../../state/modules/current-user/selectors';
import DashboardAPIManager from '../../DashboardAPIManager';

import ContextMenu from './ContextMenu';
import Toolbar from './Toolbar';
import AccessRightsContext from '../../../contexts/AccessRightsContext';

const locale = (() => {
  if (I18n.locale.startsWith('da')) {
    return daLocale;
  } else if (I18n.locale.startsWith('nl')) {
    return nlLocale;
  }

  return enLocale;
})();

const slotLabelFormat = { hour: '2-digit', minute: '2-digit', hour12: false };

// TODO
// - show negative exceptions even without an availability code
// X shrinking the calendar
// - multiple API calls when saving
// - [different PR] show warning if you don't have a schedule in this station
// X be able to remove exceptions
// - check behavior in month view
// X block editing in month and list view
// X block editing in past if you don't have access to this
// - context menu should be able to close without breaking dragging
// - fix bug when there are duty events for normal users: access denied

// TODO
// - [deferred] Extract some functionality to selector (duty function + code look up)
// - [deferred] Show past memberships as well (API returns only active)
const PersonalCalendarWidget = ({
  availabilityCodesById,
  currentUserId,
  fetchCombinedSchedules,
  fetchCurrentUser,
  fetchUsers,
  functionsById,
  groupsById,
  groupId,
  height,
  initialLoadComplete,
  membershipSchedules,
  userId,
  usersById,
  view_type,
  widgetId,
  autoResizeHeight,
  updateCombinedScheduleIntervalsInBackend,
  updateCombinedScheduleIntervalsInMemory,
  userMembershipTree
}) => {
  view_type = view_type || 'timeGridWeek';
  const [range, setRange] = useState(viewTypeInitialRange[view_type]);
  const [processing, setProcessing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(groupId || null);
  // Whenever a user clicks an interval, it will enter a "delete window"
  // If said interval is clicked again, it will be deleted
  // We use the following two variables to detect: if the interval clicked
  // has been clicked before, and if the click is within the delete window
  const [inDeleteWindow, setInDeleteWindow] = useState(false);
  const [lastClickedInterval, setLastClickedInterval] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const widgetUserId = userId || currentUserId;
  const [menuState, setMenuState] = useState({
    show: false,
    x: 0,
    y: 0,
    startTime: null,
    endTime: null,
    duty: null,
    group: null
  });

  const accessRights = useContext(AccessRightsContext);
  const isAdmin = accessRights?.admin;

  const canEdit = isAdmin && view_type === 'timeGridWeek';

  const handleContextMenu = (jsEvent, fcEvent) =>
    setMenuState({
      show: true,
      x: event.pageX,
      y: event.pageY,
      startTime: fcEvent.startStr,
      endTime: fcEvent.endStr,
      duty: fcEvent.extendedProps.duty,
      group: fcEvent.extendedProps.group
    });

  const userMemberships = useMemo(() => {
    const user = usersById[widgetUserId];
    if (!user) return [];
    return user.memberships || [];
  }, [usersById, widgetUserId]);

  const plannableStationMemberships = useMemo(() => userMemberships.filter(m => m.plannable), [userMemberships]);

  const availabilityCodes = useMemo(() => Object.values(availabilityCodesById), [availabilityCodesById]);

  const userMembershipIds = useMemo(() => userMemberships.map(m => m.id), [userMemberships]);

  const userGroupIds = useMemo(() => userMemberships.map(m => m.group_id).filter(Boolean), [userMemberships]);

  const membershipsToUpdate = useMemo(
    () => userMembershipTree.filter(m => m.group_id === selectedGroupId).flatMap(m => [m, ...m.descendantMemberships]),
    [selectedGroupId, userMembershipTree]
  );

  useEffect(() => {
    // Two modes:
    // 1. the userId is explicitly passed as an ID, in which case the schedule
    //    is shown for that specific user
    // 2. no userId is passed as a prop. The schedule is shown for the current
    //    user
    if (userId) {
      fetchUsers(userId);
    } else {
      fetchCurrentUser();
    }
  }, [fetchUsers, fetchCurrentUser, userId]);

  useEffect(() => {
    if (userGroupIds.length === 0) return;
    // Instantiate the dashboardAPIManager because this component is also used outside the dashboard
    // so the API manager can't be passed as a prop
    const dashboardAPIManager = new DashboardAPIManager();

    dashboardAPIManager.fetchStandbyDutyFunctions({ params: { group_ids: userGroupIds } });
    dashboardAPIManager.fetchGroups({ ids: userGroupIds, params: { future: true } });

    // Commented out because it causes a bug where the events can't be dragged
    // **************************************************************
    // document.addEventListener('click', (e) => {
    //   const inside = e.target.closest('.personal-calendar-context-menu');
    //   if (!inside) {
    //     setMenuState({ ...menuState, show: false });
    //   }
    // });
  }, [userGroupIds]);

  useEffect(() => {
    const title = document.querySelectorAll('.fc-toolbar-title')[0];
    if (!title) return;
    // change the date size in mobile view
    if (isMobileOnly) title.classList.add('fs-6');
  }, []);

  useEffect(() => {
    if (userGroupIds.length === 0) return;
    const dashboardAPIManager = new DashboardAPIManager();
    dashboardAPIManager.fetchAvailabilityCodes({ params: { group_ids: userGroupIds } });
  }, [userGroupIds]);

  // fetch schedule for all memberships of the user for the current displayed
  // date range
  const fetchCombinedSchedulesForRange = useCallback(() => {
    for (const id of userMembershipIds) {
      fetchCombinedSchedules({
        id,
        type: 'membership',
        start_time: range.startStr,
        end_time: range.endStr
      });
    }
  }, [fetchCombinedSchedules, range.endStr, range.startStr, userMembershipIds]);

  useEffect(fetchCombinedSchedulesForRange, [fetchCombinedSchedulesForRange]);

  // Return all schedules for the user
  const userSchedules = useMemo(
    () => Object.values(membershipSchedules).filter(s => s.user_id === widgetUserId),
    [membershipSchedules, widgetUserId]
  );

  // Returns a list of intervals which should be rendered. This is an
  // intermediate step in the processing: it gets the intervals from the
  // schedules and put them in a convenient format. The actual transformation
  // into something that FullCalendar understands is done in
  // fullCalendarEvents()
  const relevantIntervals = useMemo(
    () => computeRelevantIntervals(userSchedules, groupsById, functionsById, availabilityCodesById),
    [userSchedules, groupsById, functionsById, availabilityCodesById]
  );

  // Convert the schedule as it exists in redux to a format that
  // fullcalendar understands. https://fullcalendar.io/docs/event-object
  const fullCalendarEvents = useMemo(
    () => computeFullCalendarEvents(editMode, relevantIntervals, editMode, selectedGroupId),
    [relevantIntervals, editMode, selectedGroupId]
  );

  // START FULLCALENDAR EVENT HANDLERS

  // For details, see https://fullcalendar.io/docs/content-injection
  const eventDidMount = ({ event, el, view }) => {
    el.addEventListener('contextmenu', jsEvent => {
      jsEvent.preventDefault();
      handleContextMenu(jsEvent, event);
    });

    if (view.type === 'listWeek') {
      // replace the - incorrectly colored - event dot generated by FullCalendar
      // by our own AvailabilityIcon. This has to be done using replaceContent,
      // because there is no rendering hook in FullCalendar for this
      const dot = el.querySelectorAll('.fc-list-event-dot')[0];
      const { available, duty } = event.extendedProps;
      const betterDot = <AvailabilityIcon available={available} standbyDuty={duty} />;
      replaceContent(dot.parentElement, betterDot);
    }
  };

  // create a 1 hour interval when the user clicks in the calendar
  const createInterval = info => {
    setHasUnsavedChanges(true);
    updateIntervalInMemory({
      ...defaultInterval,
      start_time: info.dateStr,
      end_time: moment(info.dateStr).add(1, 'hour').format()
    });
  };

  const createIntervalForSelection = selectionInfo => {
    const { startStr, endStr } = selectionInfo;

    setHasUnsavedChanges(true);

    updateIntervalInMemory({
      ...defaultInterval,
      start_time: startStr,
      end_time: endStr
    });
  };

  // create a full day interval when the user clicks on a day header
  const createFullDayInterval = date => {
    const fullDayInterval = findIntervalByStartTime(date);
    if (isFullDayInterval(fullDayInterval)) toggleInterval({ event: { startStr: date } });
    else
      updateIntervalInMemory({
        ...defaultInterval,
        start_time: moment(date).startOf('day').format(),
        end_time: moment(date).startOf('day').add(1, 'day').format()
      });
  };

  const findIntervalByStartTime = intervalStartTime =>
    relevantIntervals.find(
      i => moment(i.start_time).isSame(moment(intervalStartTime)) && i.group.id === selectedGroupId
    );

  const clearInterval = interval => {
    setHasUnsavedChanges(true);
    const clearInterval = {
      ...interval,
      available: null,
      exception_available: null,
      availability_code_id: null,
      detailed_availability: { ...interval.detailed_availability, exception: null }
    };
    updateIntervalInMemory(clearInterval);
  };

  const outOfDeleteWindow = debounce(() => {
    setInDeleteWindow(false);
  }, 1000);

  const intervalDragUpdate = info => {
    const oldInterval = findIntervalByStartTime(info.oldEvent.startStr);

    if (!oldInterval) return;

    clearInterval(oldInterval);
    updateIntervalInMemory({
      ...oldInterval,
      start_time: info.event.startStr,
      end_time: info.event.endStr
    });
  };

  const toggleInterval = info => {
    setHasUnsavedChanges(true);
    const interval = findIntervalByStartTime(info.event.startStr);
    if (!interval) return;
    if (interval.availabilityCode) return;
    const duty = info.event._def?.extendedProps?.duty;
    const recurring = info.event._def?.extendedProps?.recurring;
    if (duty || recurring) return;

    // Control the logic to determine if the interval should be toggled
    // or deleted.
    // If the interval has been clicked for the first time in more than
    // 1 second, it should be toggled. If it has been clicked again within
    // 1 second, it should be deleted.
    setLastClickedInterval(interval);
    setInDeleteWindow(true);
    outOfDeleteWindow();

    if (
      inDeleteWindow &&
      !recurring &&
      !duty &&
      interval.start_time == lastClickedInterval?.start_time &&
      interval.end_time == lastClickedInterval?.end_time
    ) {
      clearInterval(interval);
      return;
    }

    const available = !interval.available;
    const updatedInterval = {
      ...interval,
      available,
      detailed_availability: { ...interval.detailed_availability, exception: available }
    };
    updateIntervalInMemory(updatedInterval);
  };
  // END FULLCALENDAR EVENT HANDLERS

  if (!widgetUserId || !initialLoadComplete) return <Loader />;

  // If height is not specifically given as a prop (such as in
  // StandaloneWidget), set it to 100% of parent
  const widgetHeight = height || '100%';

  // Selectively show an edit button or exit editing button in the toolbar
  // This button will only be shown if the user can edit the schedule
  let toolbarButtons = 'prev,next today';
  const editButton = editMode ? 'exitEditMode' : 'enterEditMode';
  if (canEdit) toolbarButtons = `${editButton} ${toolbarButtons}`;

  const headerToolbar = {
    left: toolbarButtons,
    center: 'title',
    right: widgetId ? '' : 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
  };

  const updateIntervalInMemory = interval => {
    for (const membership of membershipsToUpdate) {
      updateCombinedScheduleIntervalsInMemory(
        membership.id,
        'membership',
        [interval],
        interval.start_time,
        interval.end_time
      );
    }
  };

  const processUnsavedChanges = async ({ ignoreWarnings }) => {
    setProcessing(true);
    try {
      await updateCombinedScheduleIntervalsInBackend({ ignoreWarnings });
    } finally {
      setProcessing(false);
      setHasUnsavedChanges(false);
    }
  };

  const undoUnsavedChanges = () => {
    fetchCombinedSchedulesForRange();
    setHasUnsavedChanges(false);
  };

  const applyAvailabilityCode = code => {
    const interval = findIntervalByStartTime(menuState.startTime);
    if (!interval) return;

    const availabilityCode = code ? availabilityCodesById[code.id] : null;
    const available = availabilityCode?.available_for_crewing || (!code && interval.available);

    setHasUnsavedChanges(true);
    updateIntervalInMemory({
      ...interval,
      availability_code_id: code ? code.id : null,
      available,
      detailed_availability: { ...interval.detailed_availability, exception: available }
    });
  };

  const intervalResizeUpdate = info => {
    const oldInterval = findIntervalByStartTime(info.oldEvent.startStr);
    if (!oldInterval) return;

    clearInterval(oldInterval);
    updateIntervalInMemory({
      ...oldInterval,
      start_time: info.event.startStr,
      end_time: info.event.endStr
    });
  };

  const customButtons = {
    exitEditMode: {
      text: I18n.t('exit'),
      click: () => {
        setEditMode(false);
      }
    },
    enterEditMode: {
      text: I18n.t('edit'),
      click: () => {
        setEditMode(true);
      }
    }
  };

  const baseProps = {
    locale: locale,
    plugins: plugins,
    initialView: view_type,
    allDaySlot: false,
    slotLabelFormat: slotLabelFormat,
    slotDuration: '00:15:00',
    slotLabelInterval: { hours: 1 },
    eventContent: renderEventContent,
    eventDidMount: eventDidMount,
    nowIndicator: true,
    datesSet: setRange,
    height: isMobileOnly ? 'auto' : widgetHeight,
    headerToolbar: headerToolbar,
    events: fullCalendarEvents,
    customButtons: customButtons,
    contentHeight: autoResizeHeight ? 'auto' : widgetHeight,
    themeSystem: 'bootstrap5'
  };

  const editProps = {
    editable: true,
    eventStartEditable: true,
    eventResizableFromStart: true,
    dateClick: createInterval,
    eventResize: intervalResizeUpdate,
    eventDrop: intervalDragUpdate,
    eventClick: toggleInterval,
    eventClassNames: arg => (arg.event.extendedProps.obscured ? 'not-interactive' : ''),
    navLinks: true,
    navLinkDayClick: createFullDayInterval,
    selectable: true,
    select: createIntervalForSelection
  };

  const props = canEdit && editMode ? { ...baseProps, ...editProps } : baseProps;

  if (isEmpty(plannableStationMemberships))
    return (
      <h5 className="text-center p-2">
        <i>{I18n.t('dashboards.widget_archetypes.personal_calendar.no_schedule')}</i>
      </h5>
    );

  return (
    <>
      {editMode && (
        <>
          <ContextMenu
            menuState={menuState}
            userMemberships={userMemberships}
            availabilityCodes={availabilityCodes}
            applyAvailabilityCode={applyAvailabilityCode}
            selectedGroupId={selectedGroupId}
          />
          <Toolbar
            membershipTree={userMembershipTree}
            selectedGroupId={selectedGroupId}
            setSelectedGroupId={setSelectedGroupId}
            undoUnsavedChanges={undoUnsavedChanges}
            processUnsavedChanges={processUnsavedChanges}
            hasUnsavedChanges={hasUnsavedChanges}
            processing={processing}
            editMode={editMode}
          />
        </>
      )}
      <FullCalendar {...props} />
    </>
  );
};

Object.assign(PersonalCalendarWidget, {
  SettingsContent: Settings,
  defaultSettings: {
    view_type: 'timeGridWeek',
    autoResizeHeight: true
  }
});

const mapStateToProps = state => ({
  availabilityCodesById: availabilityCodeSelectors.byId(state),
  functionsById: functionSelectors.byId(state),
  membershipSchedules: combinedScheduleSelectors.selectMembershipCombinedScheduleState(state),
  groupsById: groupSelectors.byId(state),
  usersById: userSelectors.byId(state),
  currentUserId: selectCurrentUserId(state),
  initialLoadComplete:
    availabilityCodeSelectors.initialLoadComplete(state) &&
    functionSelectors.initialLoadComplete(state) &&
    groupSelectors.initialLoadComplete(state),
  userMembershipTree: membershipSelectors.userMembershipTree(state)
});

const mapDispatchToProps = {
  fetchCombinedSchedules: combinedScheduleOperations.fetchObjects,
  fetchCurrentUser: getCurrentUser,
  fetchUsers: userOperations.fetch,
  updateCombinedScheduleIntervalsInMemory: combinedScheduleOperations.updateIntervalsInMemory,
  updateCombinedScheduleIntervalsInBackend: combinedScheduleOperations.updateIntervalsInBackend
};

export default connect(mapStateToProps, mapDispatchToProps)(PersonalCalendarWidget);
